import React, {useEffect, useRef} from 'react';
import {Box, Stack} from '@mui/material';
import {Body} from '@lightricks/react-design-system';
import PoppaysLoader from '@/components/poppays-loader';
import {
  AssetMessageThread,
  Message,
  SendMessagePayload,
  UnifiedFeedItem,
} from '@/api/messages/types';
import {NegotiationResponse} from '@/api/messages';
import MessageListItem from './MessageListItem';
import MessageComposer from './MessageComposer';
import AssetThreadCard from './AssetThreadCard';
import styles from './MessageThread.module.scss';

interface Props {
  messages: Message[];
  feedItems?: UnifiedFeedItem[];
  firstUnreadMessageId?: string | null;
  isLoading: boolean;
  isError: boolean;
  onSend: (payload: SendMessagePayload) => Promise<unknown>;
  allowAttachments?: boolean;
  emptyState?: React.ReactNode;
  footer?: React.ReactNode;
  hasOlder?: boolean;
  isLoadingOlder?: boolean;
  onLoadOlder?: () => void;
  onOpenAssetThread?: (thread: AssetMessageThread) => void;
  onRespondToNegotiation?: (
    action: NegotiationResponse,
    brandNegotiationId: string
  ) => Promise<void>;
}

export default function MessageThread({
  messages,
  feedItems,
  firstUnreadMessageId,
  isLoading,
  isError,
  onSend,
  allowAttachments = false,
  emptyState = <Body size="sm">No messages yet. Say hello!</Body>,
  footer,
  hasOlder = false,
  isLoadingOlder = false,
  onLoadOlder,
  onOpenAssetThread,
  onRespondToNegotiation,
}: Props) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const itemCount = feedItems?.length ?? messages.length;
  const lastFeedItem = feedItems?.[feedItems.length - 1];
  let lastItemKey = messages[messages.length - 1]?.id;
  if (lastFeedItem?.kind === 'versionThread') {
    lastItemKey = `version-${lastFeedItem.data.versionId}-${lastFeedItem.data.messageCount}`;
  } else if (lastFeedItem?.kind === 'message') {
    lastItemKey = lastFeedItem.data.id;
  }

  const newMessagesDividerIndex =
    feedItems?.findIndex((item) => {
      if (item.kind === 'message') {
        return item.data.id === firstUnreadMessageId;
      }
      return item.data.messages.some(
        (message) => message.id === firstUnreadMessageId
      );
    }) ?? -1;

  const renderFeedItem = (item: UnifiedFeedItem) => {
    if (item.kind === 'versionThread') {
      if (!onOpenAssetThread) return null;
      return <AssetThreadCard thread={item.data} onOpen={onOpenAssetThread} />;
    }
    return (
      <MessageListItem
        message={item.data}
        onRespondToNegotiation={onRespondToNegotiation}
      />
    );
  };

  const renderedItems = feedItems
    ? feedItems.map((item, index) => (
        <React.Fragment
          key={
            item.kind === 'versionThread'
              ? `version-${item.data.versionId}`
              : item.data.id
          }
        >
          {index === newMessagesDividerIndex && (
            <Stack direction="row" className={styles.newMessagesDivider}>
              <span className={styles.newMessagesDividerLabel}>
                New Messages
              </span>
            </Stack>
          )}
          {renderFeedItem(item)}
        </React.Fragment>
      ))
    : messages.map((message) => (
        <MessageListItem
          key={message.id}
          message={message}
          onRespondToNegotiation={onRespondToNegotiation}
        />
      ));

  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({block: 'end'});
  }, [lastItemKey]);

  useEffect(() => {
    const sentinel = topSentinelRef.current;
    const container = scrollContainerRef.current;
    if (
      !sentinel ||
      !container ||
      !hasOlder ||
      isLoadingOlder ||
      !onLoadOlder ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return () => {};
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onLoadOlder();
      },
      {root: container, threshold: 0}
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasOlder, isLoadingOlder, onLoadOlder]);

  return (
    <Stack
      direction="column"
      className={styles.thread}
      data-testid="message-thread"
    >
      <Box className={styles.list} ref={scrollContainerRef}>
        <Stack className={styles.messagesThread}>
          <Box ref={topSentinelRef} />
          {isLoading && (
            <Stack
              direction="column"
              alignItems="center"
              justifyContent="center"
              className={styles.state}
            >
              <PoppaysLoader />
            </Stack>
          )}
          {!isLoading && isError && (
            <Stack
              direction="column"
              alignItems="center"
              justifyContent="center"
              className={styles.state}
              data-testid="message-thread-error"
            >
              <Body size="sm">Couldn’t load messages. Try again shortly.</Body>
            </Stack>
          )}
          {!isLoading && !isError && itemCount === 0 && (
            <Stack
              direction="column"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              className={`${styles.state} ${styles.emptyState}`}
              data-testid="message-thread-empty"
            >
              {emptyState}
            </Stack>
          )}
          {!isLoading && !isError && renderedItems}
          <Box ref={bottomRef} />
        </Stack>
      </Box>
      {footer && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          spacing={0.75}
          className={styles.footer}
        >
          {footer}
        </Stack>
      )}
      <MessageComposer onSend={onSend} allowAttachments={allowAttachments} />
    </Stack>
  );
}
